/**
 * Vote value normalisation.
 *
 * The canonical stored vote is `ThumbUp` / `ThumbDown` (see the API `VoteType`
 * enum and the collector's `constants/feedback.ts`). These helpers normalise to
 * a simple kind and stay tolerant of legacy `up` / `down` values so older data
 * still renders correctly.
 *
 * Sentiment follows the vote and only the vote: up = positive, down = negative.
 * A row with no thumb has no sentiment (`null`) — there is no neutral category.
 */

export type VoteKind = 'up' | 'down';
export type Sentiment = 'positive' | 'negative';

/** The vote kind, or `null` when there is no thumb (rating-only / unknown). */
export function normalizeVote(vote: string | null | undefined): VoteKind | null {
  switch ((vote ?? '').toLowerCase()) {
    case 'thumbup':
    case 'up':
      return 'up';
    case 'thumbdown':
    case 'down':
      return 'down';
    default:
      return null;
  }
}

/** Sentiment derived purely from the vote; `null` when there is no thumb. */
export function voteSentiment(vote: string | null | undefined): Sentiment | null {
  switch (normalizeVote(vote)) {
    case 'up':
      return 'positive';
    case 'down':
      return 'negative';
    default:
      return null;
  }
}

export function voteBadgeVariant(
  vote: string
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (normalizeVote(vote)) {
    case 'up':
      return 'default';
    case 'down':
      return 'destructive';
    default:
      return 'secondary';
  }
}

export function voteLabel(vote: string): string {
  switch (normalizeVote(vote)) {
    case 'up':
      return 'Up';
    case 'down':
      return 'Down';
    default:
      return vote || '—';
  }
}
